<script lang="ts" setup>
import { useUrlHistoryStore } from '~/stores/urlHistory'
import { useUsersStore } from '~/stores/users'
import {
  getGoogleTagManager,
  grantConsent,
  revokeConsent,
  type OptionalCookieName
} from '~/utils/cookie.ts'

const { t, locale, setLocale } = useI18n('components.organisms.popup.cookie-banner')

const userStore = useUsersStore()
const localeStore = useLocaleStore()
const urlHistoryStore = useUrlHistoryStore()

const cookieBanner = useTemplateRef('cookieBanner')
const cookieBannerLayer = useTemplateRef('cookieBannerLayer')

const viewport = useViewport()

const { isConsentGiven, cookiesEnabled, cookiesEnabledIds, moduleOptions } =
  useCookieControl()

const googleTagManager = getGoogleTagManager()

const cookieBarWasForcedHidden = ref(false)

const optionalCookies: OptionalCookieName[] = ['marketing-cookie', 'ads-cookie']

const necessaryCookieStatus = ref(true)
const optionalCookieStatus = ref<Record<OptionalCookieName, boolean>>({
  'marketing-cookie': false,
  'ads-cookie': false
})

const viewportIsGreaterOrEqualsTablet = computed(() =>
  viewport.isGreaterOrEqual('tablet')
)

const hasSelectedOptionalCookie = computed(() =>
  optionalCookies.some((id) => optionalCookieStatus.value[id])
)

const secondaryButtonLabelKey = computed(() =>
  hasSelectedOptionalCookie.value ? '.accept-selection' : '.accept-necessary'
)

const checkForHiddenCookieBanner = () => {
  if (cookieBarWasForcedHidden.value) {
    return
  }

  // Only run the rest of the function if consent is unknown (i.e. the banner was removed by a cookie blocker)
  if (isConsentGiven.value !== undefined) {
    return
  }

  const asideElement = cookieBanner.value?.$el.nextSibling
  const style = window.getComputedStyle(asideElement)
  const isHidden = style.getPropertyValue('display') === 'none'

  if (isHidden) {
    makePageUnscrollable(false)
    isConsentGiven.value = false
    cookieBarWasForcedHidden.value = true

    applyConsent([], true)
  }
}

const observer = new MutationObserver((mutations) => {
  if (cookieBarWasForcedHidden.value) {
    observer.disconnect()
    return
  }

  mutations.forEach(checkForHiddenCookieBanner)
})

watch(
  cookieBanner,
  (newValue) => {
    const asideElement = newValue?.$el.nextSibling

    if (!asideElement) {
      return
    }

    checkForHiddenCookieBanner()
    observer.observe(asideElement, {
      attributes: true,
      attributeFilter: ['style']
    })
  },
  { deep: true, immediate: true }
)

const applyConsent = (acceptedOptional: OptionalCookieName[], isUnknown: boolean) => {
  isConsentGiven.value = true

  const enabledOptional = moduleOptions.cookies.optional.filter((cookie) =>
    acceptedOptional.includes(cookie.id as OptionalCookieName)
  )
  cookiesEnabled.value = [...moduleOptions.cookies.necessary, ...enabledOptional]
  cookiesEnabledIds.value = cookiesEnabled.value.map((cookie) => cookie.id)

  if (enabledOptional.length > 0) {
    const acceptAdTracking = acceptedOptional.includes('ads-cookie')
    const acceptMarketingTracking = acceptedOptional.includes('marketing-cookie')

    grantConsent(googleTagManager, acceptedOptional)
    userStore.recordConsentLog('allow', {
      ads: acceptAdTracking ? 'allow' : 'deny',
      marketing: acceptMarketingTracking ? 'allow' : 'deny'
    })
  } else {
    const status = isUnknown ? 'unknown' : 'deny'

    userStore.recordConsentLog(status, {
      ads: 'deny',
      marketing: 'deny'
    })

    revokeConsent(googleTagManager)
  }
}

const onAcceptAllCookiesButtonClick = () => {
  applyConsent(optionalCookies, false)
}

const onAcceptSelectionButtonClick = () => {
  const acceptedCookies = optionalCookies.filter((id) => optionalCookieStatus.value[id])
  applyConsent(acceptedCookies, false)
}

const makePageUnscrollable = (isUnscrollable: boolean) => {
  const body = document.documentElement

  if (isUnscrollable) {
    body.classList.add(tw`overflow-y-hidden`)
    body.classList.add(tw`h-full`)
    cookieBannerLayer.value?.classList.add(tw`cookie-control-background`)
  } else {
    body.classList.remove(tw`overflow-y-hidden`)
    body.classList.remove(tw`h-full`)
    cookieBannerLayer.value?.classList.remove(tw`cookie-control-background`)
  }
}

watch(
  isConsentGiven,
  (newValue) => {
    if (newValue) {
      makePageUnscrollable(false)
    }

    if (newValue != null) {
      urlHistoryStore.hasMadeCookieDecision = true
    }
  },
  { immediate: true }
)

watch(viewportIsGreaterOrEqualsTablet, (newValue) => {
  if (isConsentGiven.value) {
    makePageUnscrollable(false)
  } else {
    makePageUnscrollable(newValue)
  }
})

const changeLocale = () => {
  const newLocale = locale.value === 'nl' ? 'en' : 'nl'

  setLocale(newLocale)
  localeStore.locale = newLocale
}

const prepareCookieBanner = async () => {
  if (!userStore.visitorId) {
    userStore.visitorId = crypto.randomUUID()
  }

  await nextTick()

  makePageUnscrollable(!isConsentGiven.value)

  const cookieBannerButtons: HTMLCollection = document.getElementsByClassName(
    'cookieControl__BarButtons'
  )
  if (cookieBannerButtons.length > 0) {
    // Remove unused 'Learn more and customize' button
    ;[...cookieBannerButtons[0].children].forEach((item) => item.remove())
    cookieBannerButtons[0].remove()
  }

  checkForHiddenCookieBanner()

  setTimeout(checkForHiddenCookieBanner, 1000)

  optionalCookieStatus.value['ads-cookie'] =
    cookiesEnabledIds.value?.includes('ads-cookie') ?? false
  optionalCookieStatus.value['marketing-cookie'] =
    cookiesEnabledIds.value?.includes('marketing-cookie') ?? false
}

watch(isConsentGiven, (newValue, oldValue) => {
  if (oldValue != null && newValue == undefined) {
    prepareCookieBanner()
  }
})

onMounted(prepareCookieBanner)
</script>

<template>
  <div v-show="!urlHistoryStore.hasMadeCookieDecision" class="h-0">
    <button
      ref="cookieBannerLayer"
      type="button"
      :aria-label="t('.overlay-button')"
      @click="makePageUnscrollable(false)"
    />
    <CookieControl ref="cookieBanner" :locale="localeStore.locale">
      <template #bar>
        <div class="flex justify-between">
          <h2 class="mr-2! section-title!">
            {{ t('.title') }}
          </h2>
          <div
            class="cursor-pointer"
            role="button"
            tabindex="0"
            @click="changeLocale"
            @keydown.enter="changeLocale"
            @keydown.space.prevent="changeLocale"
          >
            <VIcon :icon="`fi-${locale === 'nl' ? 'gb' : 'nl'}`" icon-pack="fi" />
            <span class="sr-only">
              {{ t('.change-locale_DNT') }}
            </span>
          </div>
        </div>
        <VI18n key-path=".description" class="font-sans! text-default! lg:mb-4!">
          <template #link1>
            <VLink label=".privacy-statement" :to="{ name: 'privacy-statement' }" />
          </template>
          <template #link2>
            <VLink
              href="https://policies.google.com/technologies/partner-sites"
              target="_blank"
              label="links.here"
            />
          </template>
        </VI18n>

        <div class="columns mt-4 xl:mb-4">
          <div class="column flex one-third max-xl:py-2 xl:justify-center">
            <VSwitch
              v-model="necessaryCookieStatus"
              default-value
              disabled
              size="large"
              right-label=".cookies.necessary"
              sr-label=".cookies.necessary"
              :ui="{
                base: 'max-lg:w-full max-lg:flex-row-reverse max-lg:justify-between',
                label: 'font-semibold'
              }"
            />
          </div>
          <div class="column flex one-third max-xl:py-2 xl:justify-center">
            <VSwitch
              v-model="optionalCookieStatus['marketing-cookie']"
              size="large"
              right-label=".cookies.marketing"
              sr-label=".cookies.marketing"
              :ui="{
                base: 'max-lg:w-full max-lg:flex-row-reverse max-lg:justify-between',
                label: 'font-semibold'
              }"
            />
          </div>
          <div class="column flex one-third max-xl:py-2 xl:justify-center">
            <VSwitch
              v-model="optionalCookieStatus['ads-cookie']"
              size="large"
              right-label=".cookies.ads"
              sr-label=".cookies.ads"
              :ui="{
                base: 'max-lg:w-full max-lg:flex-row-reverse max-lg:justify-between',
                label: 'font-semibold'
              }"
            />
          </div>
        </div>

        <div class="bar-buttons flex gap-x-4 max-xl:flex-col xl:justify-end">
          <VButton label=".accept-all" @click="onAcceptAllCookiesButtonClick" />
          <VButton
            :label="secondaryButtonLabelKey"
            @click="onAcceptSelectionButtonClick"
          />
        </div>
      </template>
    </CookieControl>
  </div>
</template>

<style scoped>
:deep(.cookieControl__Bar) {
  background-color: var(--ui-bg);
  border: var(--ui-border-muted) 1px solid;
  height: max-content;
  font-family: sans-serif;

  .cookieControl__BarContainer {
    flex-direction: column;

    /* Keep this styling to override the cookie banner's behaviour */
    .bar-buttons {
      > button {
        margin-block: 2px;

        &:hover {
          filter: brightness(0.85);
        }
      }

      > button:nth-of-type(1) {
        background-color: var(--color-black);
        color: var(--color-black-text);
      }

      > button:nth-of-type(2) {
        background-color: var(--color-white);
        color: var(--color-white-text);
        border: 1px solid var(--color-black);
        margin-left: 0;
      }
    }
  }
}

.dark {
  :deep(.cookieControl__Bar) {
    .bar-buttons {
      > button:nth-of-type(1) {
        background-color: var(--color-white);
        color: var(--color-white-text);
      }

      > button:nth-of-type(2) {
        border-color: var(--ui-border);
      }
    }
  }
}

:deep(.cookie-control-background) {
  position: fixed;
  inset: 0;
  backdrop-filter: blur(3px);
  z-index: 80;
}

@media (width >= 1023px) {
  :deep(.cookie-control-background) {
    backdrop-filter: blur(10px);
  }

  :deep(.cookieControl__Bar) {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: max-content;
    border-radius: var(--radius-2xl);
    overflow: hidden;
  }
}

@media (width < 1023px) {
  :deep(.cookieControl__BarContainer) {
    align-items: center;

    p {
      max-height: 20vh;
      overflow-y: scroll;
    }
  }
}
</style>
